import { CoralogixRum } from '@coralogix/browser';
import { ANY_MEWS_URL, CORALOGIX_DOMAIN, CORALOGIX_PUBLIC_KEYS } from './constants';
import { ObservabilityServiceLabels, setLabelsStrict, setUserContext, UserContext } from './observabilityDimensions';

interface CreateInstrumentationOptions {
    /**
     * Controls how much Real User Monitoring (RUM) data is collected and sent.
     * When enabled, all user sessions are tracked and recorded. When disabled, only a portion of sessions is sampled.
     * See the implementation for details.
     */
    fullSamplingEnabled: boolean;
    service: string;
    version: string;
    env: string;

    /**
     * Returns additional data to attach to telemetry events.
     * Optional if context and labels are provided elsewhere (e.g. via `Observability` class or `useObservability` hook).
     */
    getExtra?: () => Record<string, string | null> & UserContext & ObservabilityServiceLabels;

    /**
     * Whether to update labels from `getExtra` automatically before sending each event.
     *
     * @default true
     */
    updateLabelsOnBeforeSend?: boolean;

    /**
     * Whether to update user context from `getExtra` automatically before sending each event.
     *
     * @default true
     */
    updateUserContextOnBeforeSend?: boolean;
}

export const createInstrumentation = ({
    fullSamplingEnabled,
    service,
    version,
    env,
    getExtra = () => ({}),
    updateLabelsOnBeforeSend = true,
    updateUserContextOnBeforeSend = true,
}: CreateInstrumentationOptions) => {
    const publicKey = CORALOGIX_PUBLIC_KEYS[env as keyof typeof CORALOGIX_PUBLIC_KEYS];

    if (!publicKey) {
        return;
    }

    CoralogixRum.init({
        application: service,
        environment: env,
        public_key: publicKey,
        coralogixDomain: CORALOGIX_DOMAIN,
        traceParentInHeader: {
            enabled: true,
            options: {
                propagateTraceHeaderCorsUrls: [ANY_MEWS_URL],
            },
        },
        ignoreUrls: [
            /.*fullview\.io.*/, // Fullview session recording
            /.*visualstudio\.com.*/, // Application Insights
            /.*newrelic\.com.*/, // New Relic logging
            /.*nr-data\.net.*/, // New Relic error tracking
            /.*aptrinsic\.com.*/, // Gainsight/Aptrinsic analytics
            /.*getbeamer\.com.*/, // Beamer notifications
            /.*launchdarkly\.com.*/, // LaunchDarkly feature flags
            /.*cookiefirst\.com.*/, // CookieFirst consent management
        ],
        sessionConfig: {
            sessionSampleRate: fullSamplingEnabled ? 100 : 10,
            alwaysTrackSessionsWithErrors: true,
            keepSessionAfterReload: true,
        },
        sessionRecordingConfig: {
            enable: true,
            autoStartSessionRecording: true,
            recordConsoleEvents: true,
            recordCrossOriginIframes: false,
            sessionRecordingSampleRate: fullSamplingEnabled ? 100 : 20,
            maskAllInputs: true,
            maskTextSelector: '*',
        },
        version,
        beforeSend: (event) => {
            const extra = getExtra();

            if (updateUserContextOnBeforeSend) {
                setUserContext(extra);
            }

            if (updateLabelsOnBeforeSend) {
                setLabelsStrict({ env, ...extra });
            }

            return event;
        },
    });
};
